import { BLACK, type Color, KING, PAWN, TYPE_MASK, WHITE, colorBit, other, pieceColor } from '../board.ts';
import { KING_STEPS, pieceDef } from '../pieces.ts';
import { type MoveContext, rulesFor, win } from '../rules.ts';
import type { Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 2 (draft). When a pawn is captured it explodes: every non-pawn piece on the eight
// squares around it is destroyed, whichever side it belongs to. The capturing piece stands
// on the pawn's square, so it survives (unless it was an en passant capture, where the
// capturer is a pawn anyway). A king caught in a blast loses, so a capture that blows up
// the enemy king wins even if it leaves your own king in check. You can't set off a blast
// that takes out your own king.

/** Squares around each square (king steps), precomputed. */
const AROUND: number[][] = [];
for (let sq = 0; sq < 64; sq++) {
  AROUND[sq] = [];
  for (const [df, dr] of KING_STEPS) {
    const f = (sq & 7) + df;
    const r = (sq >> 3) + dr;
    if (f >= 0 && f < 8 && r >= 0 && r < 8) AROUND[sq].push(r * 8 + f);
  }
}

/** Squares a blast at `sq` would clear (non-pawn pieces only; kings included). */
export function blastVictims(board: Int8Array, sq: number): number[] {
  const out: number[] = [];
  for (const t of AROUND[sq]) {
    const p = board[t];
    if (p && (p & TYPE_MASK) !== PAWN && pieceColor(p) !== -1) out.push(t);
  }
  return out;
}

/** Pawns of `owner` that `attacker` can capture right now and that sit next to `owner`'s king. */
export function kingBlastThreats(pos: Position, owner: Color): number[] {
  const rules = rulesFor(explode);
  const k = pos.royals[owner];
  if (k < 0) return [];
  const pawn = PAWN | colorBit(owner);
  const attacker = other(owner);
  const out: number[] = [];
  for (const t of AROUND[k]) {
    if (pos.board[t] === pawn && rules.isAttacked(pos.board, t, attacker)) out.push(t);
  }
  return out;
}

export const explode: Variant = {
  id: 'explode',
  info: {
    name: 'Kaboom Chess',
    but: 'pawns explode',
    tagline: 'Capture a pawn and everything around it goes up in smoke. Kings included.',
    summary: 'When a pawn is captured it **explodes**, wiping out every non-pawn piece next to it. A king caught in the blast loses.',
    rules: [
      'When a pawn is captured, it explodes.',
      'The blast destroys every piece except pawns on the 8 squares around it, from both sides. The capturing piece survives.',
      'A king caught in a blast loses the game, even if the move that set it off left your own king in check.',
      'You can’t set off a blast that destroys your own king.',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#ff8a3d',
    emoji: '💥',
    bots: ['Sparkler', 'Firecracker', 'Dynamite', 'Demolition', 'Big Bang'],
  },
  startFen: STANDARD_FEN,
  royalKillWins: true,

  afterMove(ctx: MoveContext) {
    const { captured, capturedSq } = ctx;
    if (!captured || (captured & TYPE_MASK) !== PAWN) return;
    ctx.fx('boom', capturedSq);
    for (const t of blastVictims(ctx.next.board, capturedSq)) {
      // The piece that made the capture stands on the pawn's square, which isn't in the ring.
      ctx.remove(t, 'explosion');
    }
  },

  threatened(pos) {
    return kingBlastThreats(pos, pos.turn).length > 0;
  },

  terminal(pos) {
    if (pos.royals[WHITE] < 0) return win(BLACK, 'exploded');
    if (pos.royals[BLACK] < 0) return win(WHITE, 'exploded');
    return null;
  },

  evaluate(pos) {
    // Pieces parked next to pawns the opponent can take are a liability; kings especially.
    const rules = rulesFor(explode);
    let score = 0;
    for (let sq = 0; sq < 64; sq++) {
      const p = pos.board[sq];
      if ((p & TYPE_MASK) !== PAWN) continue;
      const owner = pieceColor(p);
      if (owner === -1) continue;
      const attacker = other(owner);
      if (!rules.isAttacked(pos.board, sq, attacker)) continue;
      const weight = pos.turn === attacker ? 0.5 : 0.15;
      for (const t of AROUND[sq]) {
        const q = pos.board[t];
        const qc = pieceColor(q);
        const qt = q & TYPE_MASK;
        if (!q || qc === -1 || qt === PAWN) continue;
        const value = qt === KING ? 3000 : pieceDef(qt).value;
        // Good for the attacker when the victim is the attacker's opponent, bad otherwise.
        const sign = qc === attacker ? -1 : 1;
        score += (attacker === WHITE ? 1 : -1) * sign * value * weight;
      }
    }
    return score;
  },

  reasonText: {
    exploded: 'The king was caught in the blast',
  },
};
